import { useForm, Controller } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { alterarSenhaSchema, AlterarSenhaFormData } from "@/pages/configuracoes/schemas";
import { useChangePassword } from "@/services/queries/useConfiguracoes";
import { SectionDivider } from "./section-divider";
import { InputPassword } from "../ui/input-password";
import { SettingsFooterActions } from "./settings-footer-actions";

interface AlterarSenhaContentProps {
  onSuccess: () => void;
  onCancel: () => void;
}

export function AlterarSenhaContent({ onSuccess, onCancel }: AlterarSenhaContentProps) {
  const changePasswordMutation = useChangePassword(onSuccess);

  const form = useForm<AlterarSenhaFormData>({
    resolver: zodResolver(alterarSenhaSchema),
    defaultValues: {
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    },
  });

  const handleSubmit = (data: AlterarSenhaFormData) => {
    changePasswordMutation.mutate(data);
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <div className="bg-[#15203c] rounded-lg p-4 flex flex-col gap-5">
        {/* Title and Description */}
        <div className="flex flex-col gap-0.5 leading-normal">
          <h2 className="font-['Plus_Jakarta_Sans'] font-bold text-xl text-white">
            Alterar Senha
          </h2>
          <p className="font-['Plus_Jakarta_Sans'] font-normal text-xs text-[#d1d1d1]">
            Atualize sua senha e mantenha conectado
          </p>
        </div>

        {/* Password Change Section */}
        <div className="flex flex-col gap-3">
          {/* Section Divider */}
          <SectionDivider title="ALTERAR SENHA" />

          <form onSubmit={form.handleSubmit(handleSubmit)} className="flex flex-col gap-4">
            {/* First Row: Old Password and New Password */}
            <div className="flex gap-4 items-start w-full">
              {/* Old Password */}
              <div className="flex flex-col gap-2 w-[721px]">
                <label className="font-['Plus_Jakarta_Sans'] font-bold text-sm text-white leading-normal">
                  Senha Antiga
                </label>
                <Controller
                  name="oldPassword"
                  control={form.control}
                  render={({ field }) => (
                    <InputPassword field={field} placeholder="Digite a senha antiga" />
                  )}
                />
                {form.formState.errors.oldPassword && (
                  <p className="text-[#dc4426] text-xs">
                    {form.formState.errors.oldPassword.message}
                  </p>
                )}
              </div>

              {/* New Password */}
              <div className="flex flex-col gap-2 w-[721px]">
                <label className="font-['Plus_Jakarta_Sans'] font-bold text-sm text-white leading-normal">
                  Nova Senha
                </label>
                <Controller
                  name="newPassword"
                  control={form.control}
                  render={({ field }) => (
                    <InputPassword field={field} placeholder="Digite a nova senha" />
                  )}
                />
                {form.formState.errors.newPassword && (
                  <p className="text-[#dc4426] text-xs">
                    {form.formState.errors.newPassword.message}
                  </p>
                )}
              </div>
            </div>

            {/* Second Row: Confirm Password */}
            <div className="flex gap-4 items-start w-full">
              {/* Confirm Password */}
              <div className="flex flex-col gap-2 w-[721px]">
                <label className="font-['Plus_Jakarta_Sans'] font-bold text-sm text-white leading-normal">
                  Confirmar Senha
                </label>
                <Controller
                  name="confirmPassword"
                  control={form.control}
                  render={({ field }) => (
                    <InputPassword field={field} placeholder="Confirme a nova senha" />
                  )}
                />
                {form.formState.errors.confirmPassword && (
                  <p className="text-[#dc4426] text-[14px]">
                    {form.formState.errors.confirmPassword.message}
                  </p>
                )}
              </div>

              {/* Empty right column for layout symmetry */}
              <div className="w-[721px]"></div>
            </div>

            {/* Forgot Password Link */}
            <div className="flex justify-start">
              <a
                href="/forgot-password"
                className="font-['Plus_Jakarta_Sans'] font-bold text-[18px] text-[#dc4426] leading-normal hover:underline"
              >
                Esqueceu sua senha?
              </a>
            </div>

            <button type="submit" className="hidden" id="alterar-senha-submit" />

            <SettingsFooterActions
              onSave={() => document.getElementById("alterar-senha-submit")?.click()}
              onCancel={onCancel}
              isSaving={changePasswordMutation.isPending}
            />
          </form>
        </div>
      </div>
    </div>
  );
}

AlterarSenhaContent.displayName = "AlterarSenhaContent";
